import {
  PHOTO_LONG_SIDE,
  estimatePdfPages,
  sendsBytes,
  uploadKindOf,
} from './importModel';

/**
 * E34 · the import's upload, prepared in the browser (block 5b): photos shrunk to about 2,000 px on the long
 * side before they leave the machine (smaller, faster — Q-e34-15 A); every file read as base64 for the one
 * import request. Over the 20 MB limit only the names, types and sizes go — the API still records the request
 * and its refusal (every upload is counted — Q-e34-23 A). Nothing is stored in the browser either.
 *
 * A photo the browser cannot decode (HEIC in most browsers) goes as it is — Vertex reads HEIC.
 */
export type UploadFile = { name: string; mimeType: string; size: number; data: string | null };

const toBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const url = String(reader.result ?? '');
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });

/** A photo on its way: shrunk when it is larger than the reader needs, as it is otherwise. */
const shrunk = async (file: File): Promise<Blob> => {
  if (typeof createImageBitmap !== 'function') return file;
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // HEIC and friends: the browser cannot draw it — Vertex reads it as it is
  }
  const long = Math.max(bitmap.width, bitmap.height);
  if (long <= PHOTO_LONG_SIDE) {
    bitmap.close();
    return file;
  }
  const scale = PHOTO_LONG_SIDE / long;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.85),
  );
  return blob ?? file;
};

export const prepareUpload = async (
  files: readonly File[],
): Promise<{ files: UploadFile[]; pages: number }> => {
  const prepared = await Promise.all(
    files.map(async (file) => {
      const blob = uploadKindOf(file) === 'image' ? await shrunk(file) : file;
      return { file, blob };
    }),
  );
  const total = prepared.reduce((n, p) => n + p.blob.size, 0);
  const withBytes = sendsBytes(total);
  let pages = files.length;
  if (files.length === 1 && uploadKindOf(files[0]) === 'pdf' && withBytes) {
    // a rough count for the progress bar only — the API counts the pages itself
    pages = estimatePdfPages(await files[0].slice(0, Math.min(files[0].size, 8 * 1024 * 1024)).text());
  }
  return {
    pages,
    files: await Promise.all(
      prepared.map(async ({ file, blob }) => ({
        name: file.name,
        mimeType: blob.type || file.type || 'application/octet-stream',
        size: blob.size,
        data: withBytes ? await toBase64(blob) : null,
      })),
    ),
  };
};
